import * as THREE from 'three'
import { PostProcessProfileA } from './PostProcessHandler'
import { SetHDRI } from './HdriHandler'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js'
import { CreateShapeFromPointsAtOnce, GetRotatedCurves, GetRotatedCurvesRectangle, CreateCurve, UvToXYZ, XYZToUV, GetPts2DData, GetModelPointsForRotationsRound, GetModelPointsForRotationsRectangle, BuildCollider } from './GeometryHandler'
import { LoadConcreteMaterial, LoadTrayMaterial, LoadFooterMaterial } from './MaterialHandler'
import { LoadMeshes, LoadCamMesh, DisplayTray, DisplayFooter } from './MeshesHandler'
import { Vector2, Vector3 } from 'three'
import { EventBus } from '@/event-bus.js'
import store from '@/store'
import Stats from 'three/examples/jsm/libs/stats.module.js'
const baseURL = process.env.VUE_APP_URL
let launchedCalc = false
let generatorfunc
let generatorStored
let generatorCalculated

// Data
const SUB_V = 93
const RADIUS = 0.0085
const SUB_PIPE = 8
const ATTRACTORS_MAX_DIST = 0.51
let TABLE_DATA = {}
let TABLE_POINTS
let angle1 = 0
let angle2 = 0
let angle3 = 0
let angle4 = 0

// Viewport
let scene
let renderer
let camera
let controls
let composer

// Materials
let shapeMaterial
let trayMaterial
let footerMaterial

// Objects
let lineLoop
let attractorA
let attractorB
let attractorC
let cameraObject
let cam1
let cam2
let cam3
let blockDamping

// Logic
let lastStep = -1
let newStep = -1
let idle = true
let idleCounter = 0
let turntable = false
let turnTableCounter = 0
const mouse = new THREE.Vector2()
let drag = false
const curvesMaterials = [
  new THREE.LineBasicMaterial({ color: 'black' }),
  new THREE.LineBasicMaterial({ color: 'black' }),
  new THREE.LineBasicMaterial({ color: 'black' }),
  new THREE.LineBasicMaterial({ color: 'black' })]

// Travelling
let angleTarget = 0
let zoomTarget = 0
let heightTarget = 0
let cameraIsTravelling = false
let curveAngle01Target
let curveAngle02Target
let curveAngle03Target
let curveAngle04Target
let attractorATarget = new Vector2(0, 0)
let attractorBTarget = new Vector2(0, 0)
let attractorCTarget = new Vector2(0, 0)
let forceATarget = 0
let forceBTarget = 0
let forceCTarget = 0

const stats = new Stats()
let statNode

let generatorCount = 0

// Exports ******************************************************************************************************

// Initialize context (scene, post process, control, materials, static geometry, dynamic geometry)
export const Init = async () => {
  SceneSettings()
  SetCamera()
  composer = PostProcessProfileA(renderer, scene, camera, window.innerWidth, window.innerHeight)
  AddAttractors()
  shapeMaterial = LoadConcreteMaterial()
  trayMaterial = LoadTrayMaterial()
  footerMaterial = LoadFooterMaterial()
  DrawCameraCircle()
  controls.addEventListener('change', () => { CameraMoveEvent() })
  await LoadMeshes(scene, trayMaterial, footerMaterial)
  cameraObject = await LoadCamMesh()
  CreateCameras()
  const texture = new THREE.TextureLoader().load(`${baseURL}/corail/assets/blop.png`)
  const geometry = new THREE.PlaneGeometry(1, 1, 1)
  const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true, opacity: 0.7 })
  const plane = new THREE.Mesh(geometry, material)
  plane.rotateX(Math.PI / 2)
  scene.add(plane)
  EventBus.$emit('SetTableFromURL')
  store.dispatch('SetAppLoaded', true)
}

// Create scene, background, camera, lights and attach to the div
function SceneSettings () {
  const container = document.getElementById('threejscontainer')
  scene = new THREE.Scene()
  scene.name = 'scene'
  renderer = new THREE.WebGLRenderer({ alpha: true })
  renderer.antialias = true
  renderer.setPixelRatio(window.devicePixelRatio)
  renderer.setSize(window.innerWidth, window.innerHeight)
  container.appendChild(renderer.domElement)
  const axesHelper = new THREE.AxesHelper(0.5)
  const light = new THREE.AmbientLight(0x404040, 0) // soft white light
  scene.add(light)
  const light2 = new THREE.PointLight(0xffffff, 0.8, 100)
  light2.position.set(0, 0, 0)
  scene.add(light2)
  // scene.add(axesHelper)
  SetHDRI(scene, renderer)
  const bg = new THREE.TextureLoader().load(
    'https://resources.roche-bobois.com/corail/assets/arriere-plan-flou-blanc-gris-clair-degrade-radial_53562-6788.jpg'
  )
  renderer.setClearColor(0xffffff, 0)
  // scene.background = new THREE.Color('white')
}

// Scene main loop
export const DrawScene = () => {
  if (turntable) {
    TurnTable()
  }
  if (idle) {
    Morphing()
  }
  updateGenerator()
  if (!cameraIsTravelling && newStep > 0 && newStep < 3 && !blockDamping) {
    controls.update()
  }
  renderer.render(scene, camera)
  composer.render()
  HandleCamRaycast()
  if (lastStep === 2) {
    HandleRaycast()
    HandleDragRaycast()
    EventBus.$emit('PtScreenPositionUpdated', GetPts2DData(camera, scene.getObjectByName('collider'), attractorA.position, attractorB.position, attractorC.position))
  }
  if (cameraIsTravelling) {
    PerformTravelling()
  }
  if (store.getters.isDebug) {
    stats.update()
  }
}

function CreateCameras () {
  cam1 = cameraObject.clone()
  cam2 = cameraObject.clone()
  cam3 = cameraObject.clone()
  const col = 'rgb(120, 120, 120)'
  cam1.children[0].material = new THREE.MeshStandardMaterial({ color: col })
  cam2.children[0].material = new THREE.MeshStandardMaterial({ color: col })
  cam3.children[0].material = new THREE.MeshStandardMaterial({ color: col })
  scene.add(cam1)
  scene.add(cam2)
  scene.add(cam3)
  const scale = 0.05
  cam1.position.set(0.7, 0.06, 0)
  cam2.position.set(-0.35, 0.06, 0.61)
  cam3.position.set(-0.35, 0.06, -0.61)
  cam1.scale.set(scale, scale, scale)
  cam2.scale.set(scale, scale, scale)
  cam3.scale.set(scale, scale, scale)
  cam1.lookAt(new Vector3(0, 0.06, 0))
  cam2.lookAt(new Vector3(0, 0.06, 0))
  cam3.lookAt(new Vector3(0, 0.06, 0))
  cam1.rotateY(-90 * (Math.PI / 180))
  cam2.rotateY(-90 * (Math.PI / 180))
  cam3.rotateY(-90 * (Math.PI / 180))
  cam1.name = 'cam1'
  cam2.name = 'cam2'
  cam3.name = 'cam3'
}

export const SetDebugStats = (debug) => {
  if (debug) {
    statNode = document.getElementById('threejscontainer').appendChild(stats.dom)
  } else {
    if (statNode) {
      document.getElementById('threejscontainer').removeChild(stats.dom)
      statNode = undefined
    }
  }
}

function updateGenerator () {
  if (launchedCalc) {
    /*
    if (newStep > 0) {
      generatorCount++
      if (generatorCount < 4) {
        return
      } else {
        generatorCount = 0
      }
    }
    */
    const a = generatorfunc.next().value
    if (a) {
      const mesh = a
      const shapeInScene = scene.getObjectByName('shape')
      if (shapeInScene) {
        scene.remove(shapeInScene)
      }
      mesh.material = shapeMaterial
      mesh.name = 'shape'
      scene.add(mesh)
      launchedCalc = false
      if (newStep === 1) {
        DrawCurves()
      }
      RebuildCollider()
      if (generatorStored && JSON.stringify(generatorStored) !== JSON.stringify(generatorCalculated)) {
        if (generatorStored.rotation1 !== store.getters.curve01 ||
          generatorStored.rotation2 !== store.getters.curve02 ||
          generatorStored.rotation3 !== store.getters.curve03 ||
          generatorStored.rotation4 !== store.getters.curve04) {
          UpdateShape(store.getters.curve01,
            store.getters.curve02,
            store.getters.curve03,
            store.getters.curve04,
            store.getters.u1,
            store.getters.v1,
            store.getters.u2,
            store.getters.v2,
            store.getters.u3,
            store.getters.v3,
            store.getters.force01,
            store.getters.force02,
            store.getters.force03)
        } else {
          UpdateTexture(
            generatorStored.u1, generatorStored.v1,
            generatorStored.u2, generatorStored.v2,
            generatorStored.u3, generatorStored.v3,
            generatorStored.force1, generatorStored.force2, generatorStored.force3
          )
        }
      } else {
        store.dispatch('SetLoading', false)
      }
    }
  }
}

function RebuildCollider () {
  const collider = scene.getObjectByName('collider')
  if (collider) {
    scene.remove(collider)
  }
  const mesh = BuildCollider(TABLE_POINTS)
  mesh.name = 'collider'
  scene.add(mesh)
}

export const Zoom = (val) => {
  if (cameraIsTravelling) {
    cameraIsTravelling = false
  }
  angleTarget = GetPlanarAngle(camera.position.x, camera.position.z)
  zoomTarget = Math.sqrt(Math.pow(camera.position.x, 2) + Math.pow(camera.position.z, 2))
  zoomTarget -= val
  heightTarget = camera.position.y
  cameraIsTravelling = true
}

export const LoadTable = (table) => {
  TABLE_DATA = table
  DisplayTray(scene, TABLE_DATA.trayMesh)
  DisplayFooter(scene, TABLE_DATA.footerMesh)
  UpdateShape(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0)
  idleCounter = 0
}

export const UpdateShape = (rotation1, rotation2, rotation3, rotation4, u1, v1, u2, v2, u3, v3, force1, force2, force3) => {
  angle1 = rotation1
  angle2 = rotation2
  angle3 = rotation3
  angle4 = rotation4
  if (TABLE_DATA.curves) {
    if (TABLE_DATA.type === 'round') {
      TABLE_POINTS = GetModelPointsForRotationsRound(TABLE_DATA.curves, rotation1, rotation2, rotation3, rotation4, TABLE_DATA.subdivisions, SUB_V)
      UpdateTexture(u1, v1, u2, v2, u3, v3, force1, force2, force3)
    } else if (TABLE_DATA.type === 'rectangular') {
      TABLE_POINTS = GetModelPointsForRotationsRectangle(TABLE_DATA.curves, rotation1, rotation2, rotation3, rotation4, TABLE_DATA.subdivisions, SUB_V)
      UpdateTexture(u1, v1, u2, v2, u3, v3, force1, force2, force3)
    }
  }
}

export const UpdateTexture = (u1, v1, u2, v2, u3, v3, force1, force2, force3) => {
  if (TABLE_POINTS) {
    MoveAttractor(0, u1, v1)
    MoveAttractor(1, u2, v2)
    MoveAttractor(2, u3, v3)
    if (!launchedCalc) {
      launchedCalc = true
      generatorCount = 0
      store.dispatch('SetLoading', true)
      generatorCalculated = {
        rotation1: angle1, rotation2: angle2, rotation3: angle3, rotation4: angle4, u1: u1, v1: v1, u2: u2, v2: v2, u3: u3, v3: v3, force1: force1, force2: force2, force3: force3
      }
      generatorStored = {
        rotation1: angle1, rotation2: angle2, rotation3: angle3, rotation4: angle4, u1: u1, v1: v1, u2: u2, v2: v2, u3: u3, v3: v3, force1: force1, force2: force2, force3: force3
      }
      generatorfunc = CreateShapeFromPointsAtOnce(TABLE_POINTS, RADIUS, SUB_PIPE, attractorA.position, attractorB.position, attractorC.position, force1, force2, force3, ATTRACTORS_MAX_DIST, TABLE_DATA.extrusion)
    } else {
      generatorStored = {
        rotation1: store.getters.curve01, rotation2: store.getters.curve02, rotation3: store.getters.curve03, rotation4: store.getters.curve04, u1: u1, v1: v1, u2: u2, v2: v2, u3: u3, v3: v3, force1: force1, force2: force2, force3: force3
      }
    }
  }
}

function CameraMoveEvent () {
  cameraIsTravelling = false
  store.dispatch('SetHighlightedPt', -1)
}

function PerformTravelling () {
  const cameraAngleXZ = GetPlanarAngle(camera.position.x, camera.position.z)
  const height = camera.position.y
  const heightDiff = height - heightTarget
  const hyp = Math.sqrt(Math.pow(camera.position.x, 2) + Math.pow(camera.position.z, 2))
  const hypDiff = zoomTarget - hyp
  const diff = angleTarget - cameraAngleXZ
  if (Math.abs(diff) > 0.1 || Math.abs(heightDiff) > 0.1 || Math.abs(hypDiff) > 0.1) {
    let target = 0
    if (Math.abs(diff) > 180) {
      target = (cameraAngleXZ - (diff / 50)) * (Math.PI / 180)
    } else {
      target = (cameraAngleXZ + (diff / 50)) * (Math.PI / 180)
    }
    let x = Math.sin(target) * hyp
    let z = Math.cos(target) * hyp
    x = Math.sin(target) * (hyp + (hypDiff / 50))
    z = Math.cos(target) * (hyp + (hypDiff / 50))
    camera.position.set(x, camera.position.y - heightDiff / 50, z)
    camera.lookAt(new THREE.Vector3(0, 0.4, 0))
  } else {
    cameraIsTravelling = false
  }
}

function GetPlanarAngle (x, z) {
  const angleRad = Math.atan(x / z)
  let angleDeg = angleRad * (180 / Math.PI)
  if (z < 0 && x > 0) {
    angleDeg = 90 + (90 + angleDeg)
  } else if (z < 0 && x < 0) {
    angleDeg = 180 + angleDeg
  } else if (z > 0 && x < 0) {
    angleDeg = 360 + angleDeg
  }
  return angleDeg
}

export const SetStep = (step) => {
  blockDamping = false
  newStep = step
  idle = false
  turntable = false
  SetCameraGroupVisibility(true)
  store.dispatch('SetLoading', false)
  RemoveCurves()
  switch (step) {
    case 0:
      SetCameraGroupVisibility(false)
      idle = true
      controls.enabled = false
      camera.position.set(2.4, 0.8, 0)
      camera.lookAt(new THREE.Vector3(0, 0.28, 0))
      cameraIsTravelling = false
      camera.fov = 55
      break
    case 1:
      if (lastStep === 0) {
        LoadTable(TABLE_DATA)
      } else {
        DrawCurves()
      }
      if (lastStep === 3) {
        camera.position.set(2, 0.75, 0)
      }
      controls.enabled = true
      camera.lookAt(controls.target)
      camera.fov = 55
      break
    case 2:
      if (lastStep === 3) {
        camera.position.set(2, 0.75, 0)
      }
      controls.enabled = true
      camera.lookAt(controls.target)
      camera.fov = 55
      break
    case 3:
      turnTableCounter = 0
      turntable = true
      SetCameraGroupVisibility(false)
      cameraIsTravelling = false
      controls.enabled = false
      camera.position.set(5, 1.3, -0.45)
      camera.lookAt(new THREE.Vector3(0, 0.51, -0.45))
      camera.fov = 20
      break
  }
  ResetRotation()
  camera.updateProjectionMatrix()
  lastStep = step
}

export const TakeScreenShot = (resX, resY, view) => {
  const s = scene.background
  const stockTurnTable = turnTableCounter
  const shapeInScene = scene.getObjectByName('shape')
  const tray = scene.getObjectByName(TABLE_DATA.trayMesh)
  const footer = scene.getObjectByName(TABLE_DATA.footerMesh)
  let storedMat = null
  let storedMat2 = null
  if (shapeInScene) {
    shapeInScene.rotation.set(0, 90 * (Math.PI / 180), 0)
  }
  if (tray) {
    tray.rotation.set(0, 90 * (Math.PI / 180), 0)
    storedMat = tray.children[0].material.clone()
  }
  if (footer) {
    footer.rotation.set(0, 90 * (Math.PI / 180), 0)
    storedMat2 = footer.children[0].material.clone()
  }
  scene.background = new THREE.Color('white')
  const cameraStore = camera
  const orthoFact = 130
  tray.children[0].material.opacity = 0.6
  switch (view) {
    case 'final':
      camera.position.set(-3.1, 1, 2.9)
      camera.lookAt(new THREE.Vector3(0, 0.37, 0))
      camera.fov = 16
      break
    case 'top':
      camera.position.set(0, 10, 0)
      camera.lookAt(new THREE.Vector3(0, 0.30, 0))
      camera.fov = 17
      tray.children[0].material.opacity = 0.2
      tray.children[0].material.transparent = true
      tray.children[0].material.metalness = 0
      tray.children[0].material.roughness = 1
      tray.children[0].material.reflectivity = 0
      tray.children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
      footer.children[0].material.color = new THREE.Color('rgb(150, 150, 150)')
      break
    case 'persp':
      camera.position.set(-3.1, 1, 2.9)
      camera.lookAt(new THREE.Vector3(0, 0.37, 0))
      camera.fov = 20
      tray.children[0].material.metalness = 0
      tray.children[0].material.roughness = 0.52
      tray.children[0].material.reflectivity = 0
      tray.children[0].material.color = new THREE.Color('rgb(132, 138, 135)')
      footer.children[0].material.color = new THREE.Color('rgb(80, 80, 80)')
      break
    case 'left':
      camera = new THREE.OrthographicCamera(-resX / orthoFact, resX / orthoFact, resY / orthoFact, -resY / orthoFact, 1, 1000)
      camera.position.set(0, 0.4, 2)
      camera.lookAt(new THREE.Vector3(0, 0.4, 0))
      camera.fov = 40
      tray.children[0].material.opacity = 1
      tray.children[0].material.transparent = false
      tray.children[0].material.metalness = 0
      tray.children[0].material.roughness = 1
      tray.children[0].material.reflectivity = 0
      tray.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      footer.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      break
    case 'front':
      camera = new THREE.OrthographicCamera(-resX / orthoFact, resX / orthoFact, resY / orthoFact, -resY / orthoFact, 1, 1000)
      camera.position.set(4, 0.4, 0)
      camera.lookAt(new THREE.Vector3(0, 0.4, 0))
      camera.fov = 40
      tray.children[0].material.opacity = 1
      tray.children[0].material.transparent = false
      tray.children[0].material.metalness = 0
      tray.children[0].material.roughness = 1
      tray.children[0].material.reflectivity = 0
      tray.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      footer.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      break
    case 'right':
      camera = new THREE.OrthographicCamera(-resX / orthoFact, resX / orthoFact, resY / orthoFact, -resY / orthoFact, 1, 1000)
      camera.position.set(0, 0.4, -2)
      camera.lookAt(new THREE.Vector3(0, 0.4, 0))
      camera.fov = 40
      tray.children[0].material.opacity = 1
      tray.children[0].material.transparent = false
      tray.children[0].material.metalness = 0
      tray.children[0].material.roughness = 1
      tray.children[0].material.reflectivity = 0
      tray.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      footer.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      break
    case 'back':
      camera = new THREE.OrthographicCamera(-resX / orthoFact, resX / orthoFact, resY / orthoFact, -resY / orthoFact, 1, 1000)
      camera.position.set(-4, 0.4, 0)
      camera.lookAt(new THREE.Vector3(0, 0.4, 0))
      camera.fov = 40
      tray.children[0].material.opacity = 1
      tray.children[0].material.transparent = false
      tray.children[0].material.metalness = 0
      tray.children[0].material.roughness = 1
      tray.children[0].material.reflectivity = 0
      tray.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      footer.children[0].material.color = new THREE.Color('rgb(50, 50, 50)')
      break
  }
  resX = resX * 2
  resY = resY * 2
  camera.aspect = resX / resY
  camera.updateProjectionMatrix()
  renderer.setSize(resX * 2, resY * 2)
  composer = PostProcessProfileA(renderer, scene, camera, resX * 2, resY * 2)
  renderer.render(scene, camera)
  composer.render()
  const dom = renderer.domElement.toDataURL('image/jpeg', 0.95)
  scene.background = s
  camera = cameraStore
  turnTableCounter = stockTurnTable
  if (shapeInScene) {
    shapeInScene.rotation.set(0, turnTableCounter, 0)
  }
  if (tray) {
    tray.rotation.set(0, turnTableCounter, 0)
    tray.children[0].material = storedMat
  }
  if (footer) {
    footer.rotation.set(0, turnTableCounter, 0)
    footer.children[0].material = storedMat2
  }
  ResetCameraSave()
  return dom
}

function ResetCameraSave () {
  camera.position.set(5, 1.3, -0.45)
  camera.lookAt(new THREE.Vector3(0, 0.51, -0.45))
  camera.fov = 20
  onWindowResize()
}

function SetCameraGroupVisibility (visible) {
  lineLoop.visible = visible
  cam1.visible = visible
  cam2.visible = visible
  cam3.visible = visible
}

function TurnTable () {
  turnTableCounter += 0.003
  const shapeInScene = scene.getObjectByName('shape')
  const tray = scene.getObjectByName(TABLE_DATA.trayMesh)
  const footer = scene.getObjectByName(TABLE_DATA.footerMesh)
  if (shapeInScene) {
    shapeInScene.rotation.set(0, turnTableCounter, 0)
  }
  if (tray) {
    tray.rotation.set(0, turnTableCounter, 0)
  }
  if (footer) {
    footer.rotation.set(0, turnTableCounter, 0)
  }
}

function ResetRotation () {
  const shapeInScene = scene.getObjectByName('shape')
  const tray = scene.getObjectByName(TABLE_DATA.trayMesh)
  const footer = scene.getObjectByName(TABLE_DATA.footerMesh)
  if (shapeInScene) {
    shapeInScene.rotation.set(0, 0, 0)
  }
  if (tray) {
    tray.rotation.set(0, 0, 0)
  }
  if (footer) {
    footer.rotation.set(0, 0, 0)
  }
}

function Morphing () {
  if (!TABLE_DATA.curves) return
  idleCounter++
  if (idleCounter > 120) {
    idleCounter = 0
    SetRandomTargets()
    UpdateShape(
      curveAngle01Target,
      curveAngle02Target,
      curveAngle03Target,
      curveAngle04Target,
      attractorATarget.x, attractorATarget.y,
      attractorBTarget.x, attractorBTarget.y,
      attractorCTarget.x, attractorCTarget.y,
      forceATarget,
      forceBTarget,
      forceCTarget
    )
  }
}

function SetRandomTargets () {
  curveAngle01Target = (Math.random() * 24) - 12
  curveAngle02Target = (Math.random() * 24) - 12
  curveAngle03Target = (Math.random() * 24) - 12
  curveAngle04Target = (Math.random() * 24) - 12
  attractorATarget = new THREE.Vector2(Math.random(), Math.random())
  attractorBTarget = new THREE.Vector2(Math.random(), Math.random())
  attractorCTarget = new THREE.Vector2(Math.random(), Math.random())
  forceATarget = Math.random() * 0.8
  forceBTarget = Math.random() * 0.6
  forceCTarget = Math.random() * 0.4
}

// Move an attractor point
export const MoveAttractor = (index, u, v) => {
  blockDamping = true
  const pos = UvToXYZ(TABLE_POINTS, u, v)
  switch (index) {
    case 0:
      attractorA.position.set(pos.x, pos.y, pos.z)
      break
    case 1:
      attractorB.position.set(pos.x, pos.y, pos.z)
      break
    case 2:
      attractorC.position.set(pos.x, pos.y, pos.z)
      break
  }
}

// Update camera and rendere size when window changes size
export const onWindowResize = () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
  composer = PostProcessProfileA(renderer, scene, camera, window.innerWidth, window.innerHeight)
}

// Track mouse position and stores it in the mouse variable
export const onMouseMove = (event) => {
  if (!event) return
  mouse.x = ((event.clientX || event.touches[0].clientX) / window.innerWidth) * 2 - 1
  mouse.y = -((event.clientY || event.touches[0].clientY) / window.innerHeight) * 2 + 1
}

export const EndDrag = () => {
  drag = false
}

// Raycast once (for touch screens, no hovering)
export const HandleRaycastOnce = (event) => {
  if (cameraIsTravelling) {
    cameraIsTravelling = false
  }
  mouse.x = ((event.clientX || event.touches[0].clientX) / window.innerWidth) * 2 - 1
  mouse.y = -((event.clientY || event.touches[0].clientY) / window.innerHeight) * 2 + 1
  if (store.getters.selectedPt > -1) {
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, camera)
    const intersects = raycaster.intersectObjects([scene.getObjectByName('shape')])
    if (intersects.length > 0) {
      const point = intersects[0].point
      switch (store.getters.selectedPt) {
        case 0:
          attractorA.position.set(point.x, point.y, point.z)
          store.dispatch('SetSelectedPt', -1)
          break
        case 1:
          attractorB.position.set(point.x, point.y, point.z)
          store.dispatch('SetSelectedPt', -1)
          break
        case 2:
          attractorC.position.set(point.x, point.y, point.z)
          store.dispatch('SetSelectedPt', -1)
          break
      }
      const AUV = GetAttractorUVPosition(0)
      const BUV = GetAttractorUVPosition(1)
      const CUV = GetAttractorUVPosition(2)
      store.dispatch('SetUVs', [AUV.u, AUV.v, BUV.u, BUV.v, CUV.u, CUV.v])
    }
  }
  if (newStep === 1 || newStep === 2) {
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, camera)
    const intersectsCam = raycaster.intersectObjects([scene.getObjectByName('cam1').children[0], scene.getObjectByName('cam2').children[0], scene.getObjectByName('cam3').children[0]])
    if (intersectsCam.length > 0) {
      switch (intersectsCam[0].object.parent.name) {
        case 'cam1':
          GoToPOV(0)
          break
        case 'cam2':
          GoToPOV(1)
          break
        case 'cam3':
          GoToPOV(2)
          break
      }
    }
  }
}

// Returns UV position of an attractor point
export const GetAttractorUVPosition = (index) => {
  switch (index) {
    case 0:
      return XYZToUV(TABLE_POINTS, attractorA.position.x, attractorA.position.y, attractorA.position.z)
    case 1:
      return XYZToUV(TABLE_POINTS, attractorB.position.x, attractorB.position.y, attractorB.position.z)
    case 2:
      return XYZToUV(TABLE_POINTS, attractorC.position.x, attractorC.position.y, attractorC.position.z)
  }
}

export const HighlightSlider = (index) => {
  // WARNING : LINEWIDTH NOT SUPPORTED
  for (let i = 0; i < curvesMaterials.length; i++) {
    if (i === index) {
      curvesMaterials[i] = new THREE.LineBasicMaterial({ color: 'rgb(252, 177, 2)', linewidth: 3 })
    } else {
      curvesMaterials[i] = new THREE.LineBasicMaterial({ color: 'black', linewidth: 1 })
    }
  }
  // RemoveCurves()
  if (!launchedCalc) {
    DrawCurves()
  }
}

export const DownloadMesh = (name) => {
  const exporter = new OBJExporter()
  const data = exporter.parse(scene.getObjectByName('shape'))
  download(name + '.obj', data)
}
// Internal ******************************************************************************************************
function download (filename, text) {
  const element = document.createElement('a')
  element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text))
  element.setAttribute('download', filename)

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

// Init attractor points
function AddAttractors () {
  const geometry = new THREE.SphereGeometry(0.015, 32, 32)
  const materialR = new THREE.MeshBasicMaterial({ color: 'red' })
  const materialG = new THREE.MeshBasicMaterial({ color: 'blue' })
  const materialB = new THREE.MeshBasicMaterial({ color: 'green' })
  attractorA = new THREE.Mesh(geometry, materialR)
  attractorB = new THREE.Mesh(geometry, materialG)
  attractorC = new THREE.Mesh(geometry, materialB)
  attractorA.visible = false
  attractorB.visible = false
  attractorC.visible = false
  scene.add(attractorA)
  scene.add(attractorB)
  scene.add(attractorC)
}

// Draw dynamic curves
function DrawCurves () {
  let curves = []
  if (TABLE_DATA.type === 'round') {
    curves = GetRotatedCurves(TABLE_DATA.curves, store.getters.curve01, store.getters.curve02, store.getters.curve03, store.getters.curve04)
  } else if (TABLE_DATA.type === 'rectangular') {
    curves = GetRotatedCurvesRectangle(TABLE_DATA.curves, store.getters.curve01, store.getters.curve02, store.getters.curve03, store.getters.curve04)
  }
  DrawCurve(curves[0], 'curve01', curvesMaterials[0])
  DrawCurve(curves[1], 'curve02', curvesMaterials[1])
  DrawCurve(curves[2], 'curve03', curvesMaterials[2])
  DrawCurve(curves[3], 'curve04', curvesMaterials[3])
}

// Draw dynamic curve
function DrawCurve (curve, name, mat) {
  const curve01InScene = scene.getObjectByName(name)
  if (curve01InScene) {
    scene.remove(curve01InScene)
  }
  const curve01 = CreateCurve(curve, mat, 200, 0.02)
  curve01.name = name
  scene.add(curve01)
}

function RemoveCurves () {
  const curve01InScene = scene.getObjectByName('curve01')
  if (curve01InScene) {
    scene.remove(curve01InScene)
  }
  const curve02InScene = scene.getObjectByName('curve02')
  if (curve02InScene) {
    scene.remove(curve02InScene)
  }
  const curve03InScene = scene.getObjectByName('curve03')
  if (curve03InScene) {
    scene.remove(curve03InScene)
  }
  const curve04InScene = scene.getObjectByName('curve04')
  if (curve04InScene) {
    scene.remove(curve04InScene)
  }
}

// Raycast (mouse control, with hover)
function HandleRaycast () {
  if (store.getters.selectedPt > -1) {
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, camera)
    const intersects = raycaster.intersectObjects([scene.getObjectByName('shape')])
    if (intersects.length > 0) {
      const point = intersects[0].point
      switch (store.getters.selectedPt) {
        case 0:
          attractorA.position.set(point.x, point.y, point.z)
          break
        case 1:
          attractorB.position.set(point.x, point.y, point.z)
          break
        case 2:
          attractorC.position.set(point.x, point.y, point.z)
          break
      }
      const AUV = GetAttractorUVPosition(0)
      const BUV = GetAttractorUVPosition(1)
      const CUV = GetAttractorUVPosition(2)
      store.dispatch('SetUVs', [AUV.u, AUV.v, BUV.u, BUV.v, CUV.u, CUV.v])
    }
  }
}

function HandleCamRaycast () {
  if (newStep === 1 || newStep === 2) {
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, camera)
    const intersectsCam = raycaster.intersectObjects([scene.getObjectByName('cam1').children[0], scene.getObjectByName('cam2').children[0], scene.getObjectByName('cam3').children[0]])
    if (intersectsCam.length > 0) {
      switch (intersectsCam[0].object.parent.name) {
        case 'cam1':
          scene.getObjectByName('cam1').children[0].material.color = new THREE.Color('rgb(180, 180, 180)')
          scene.getObjectByName('cam2').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
          scene.getObjectByName('cam3').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
          break
        case 'cam2':
          scene.getObjectByName('cam2').children[0].material.color = new THREE.Color('rgb(180, 180, 180)')
          scene.getObjectByName('cam1').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
          scene.getObjectByName('cam3').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
          break
        case 'cam3':
          scene.getObjectByName('cam3').children[0].material.color = new THREE.Color('rgb(180, 180, 180)')
          scene.getObjectByName('cam1').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
          scene.getObjectByName('cam2').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
          break
      }
    } else {
      scene.getObjectByName('cam1').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
      scene.getObjectByName('cam2').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
      scene.getObjectByName('cam3').children[0].material.color = new THREE.Color('rgb(120, 120, 120)')
    }
  }
}

export const SetDragOn = (index) => {
  drag = true
  store.dispatch('SetHighlightedPt', index)
}

// Raycast (mouse control, with hover)
function HandleDragRaycast () {
  if (store.getters.highlightedPt > -1 && drag) {
    cameraIsTravelling = false
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, camera)
    const intersects = raycaster.intersectObjects([scene.getObjectByName('shape')])
    if (intersects.length > 0) {
      const point = intersects[0].point
      switch (store.getters.highlightedPt) {
        case 0:
          attractorA.position.set(point.x, point.y, point.z)
          break
        case 1:
          attractorB.position.set(point.x, point.y, point.z)
          break
        case 2:
          attractorC.position.set(point.x, point.y, point.z)
          break
      }
      const AUV = GetAttractorUVPosition(0)
      const BUV = GetAttractorUVPosition(1)
      const CUV = GetAttractorUVPosition(2)
      store.dispatch('SetUVs', [AUV.u, AUV.v, BUV.u, BUV.v, CUV.u, CUV.v])
    }
  }
}

function DrawCameraCircle () {
  const radius = 0.7
  const segments = 64
  const material = new THREE.LineDashedMaterial({
    color: 0x000000,
    linewidth: 1,
    scale: 200,
    dashSize: 1,
    gapSize: 1
  })
  const geometry = new THREE.CircleGeometry(radius, segments)
  // Remove center vertex×
  geometry.vertices.shift()
  lineLoop = new THREE.LineLoop(geometry, material)
  lineLoop.computeLineDistances()
  scene.add(lineLoop)
  lineLoop.rotateX(90 * (Math.PI / 180))
}

function SetCamera () {
  camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.01, 1000)
  ResetControls()
}

function ResetControls () {
  controls = new OrbitControls(camera, renderer.domElement)
  controls.minPolarAngle = Math.PI * 0
  controls.maxPolarAngle = Math.PI * 0.6
  controls.target.set(0, 0.4, 0)
  controls.minDistance = 1.1
  controls.maxDistance = 2.5
  controls.enablePan = false
  controls.enableDamping = true
  controls.dampingFactor = 0.05
  controls.update()
}

export const GoToPOV = (index) => {
  switch (index) {
    case 0:
      angleTarget = 90
      zoomTarget = 1.2
      heightTarget = 0.4
      break
    case 1:
      angleTarget = 330
      zoomTarget = 1.2
      heightTarget = 0.4
      break
    case 2:
      angleTarget = 210
      zoomTarget = 1.2
      heightTarget = 0.4
      break
  }
  cameraIsTravelling = true
}

export const FocusOnPt = (index) => {
  if (index < 0) {
    blockDamping = false
  }
  if (index >= 0 && index < 3) {
    switch (index) {
      case 0:
        angleTarget = GetPlanarAngle(attractorA.position.x, attractorA.position.z)
        zoomTarget = 1.2
        heightTarget = attractorA.position.y
        break
      case 1:
        angleTarget = GetPlanarAngle(attractorB.position.x, attractorB.position.z)
        zoomTarget = 1.2
        heightTarget = attractorB.position.y
        break
      case 2:
        angleTarget = GetPlanarAngle(attractorC.position.x, attractorC.position.z)
        zoomTarget = 1.2
        heightTarget = attractorC.position.y
        break
    }
    cameraIsTravelling = true
  }
}
